import React, { useState, useEffect } from 'react';
import { getRestoredInstances, deleteRestoredInstance, updateInstanceStatus } from '../services/dynamoService';
import { deleteInstance, getInstanceDetails } from '../services/rdsService';
import InstanceDetailsModal from './InstanceDetailsModal';

const RestoredInstances = ({ awsCredentials }) => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);

  useEffect(() => {
    loadInstances();
  }, [awsCredentials]);

  const loadInstances = async () => {
    try {
      setLoading(true);
      const data = await getRestoredInstances(awsCredentials);

      // Update status and endpoint for each instance from AWS
      const updatedInstances = await Promise.all(
        data.map(async (instance) => {
          try {
            // Get current status from AWS
            const awsInstance = await getInstanceDetails(instance.instanceId, awsCredentials);

            if (awsInstance) {
              // Update DynamoDB with latest info
              await updateInstanceStatus(instance.instanceId, awsInstance, awsCredentials);
              
              // Return updated instance data
              return {
                ...instance,
                status: awsInstance.DBInstanceStatus,
                endpoint: awsInstance.Endpoint?.Address || instance.endpoint,
                port: awsInstance.Endpoint?.Port || instance.port,
                engine: awsInstance.Engine || instance.engine,
                instanceClass: awsInstance.DBInstanceClass || instance.instanceClass
              };
            }
            return instance;
          } catch (err) {
            console.error(`Error updating instance ${instance.instanceId}:`, err);
            return instance; // Return original if update fails
          }
        })
      );
      
      setInstances(updatedInstances);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInstance = async (instanceId) => {
    if (!window.confirm(`Are you sure you want to delete instance ${instanceId}? This will permanently delete the database instance without creating a final snapshot.`)) {
      return;
    }

    try {
      await deleteInstance(instanceId, true, awsCredentials);
      await deleteRestoredInstance(instanceId, awsCredentials);
      await loadInstances();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyEndpoint = (endpoint, event) => {
    navigator.clipboard.writeText(endpoint).then(() => {
      // Create a temporary success indication
      const button = event.target.closest('.aws-copy-button');
      if (button) {
        const originalContent = button.innerHTML;
        button.innerHTML = '✓';
        button.style.color = '#037f0c';
        
        setTimeout(() => {
          button.innerHTML = originalContent;
          button.style.color = '';
        }, 1000);
      }
    }).catch(() => {
      alert('Failed to copy endpoint. Please copy manually: ' + endpoint);
    });
  };

  if (loading) {
    return (
      <div className="aws-loading">
        <div className="aws-spinner"></div>
        Loading restored instances...
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="aws-alert aws-alert-error">
        <strong>Error loading instances:</strong> {error}
      </div>
    );
  }

  return (
    <div>
      {/* Actions bar */}
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <button 
            className="aws-button aws-button-secondary"
            onClick={loadInstances}
            disabled={loading}
          >
            {loading ? (
              <span>
                <span className="aws-spinner" style={{ 
                  width: '12px', 
                  height: '12px', 
                  marginRight: '6px',
                  display: 'inline-block' 
                }}></span>
                Checking AWS...
              </span>
            ) : (
              '↻ Refresh'
            )}
          </button>
          <span className="aws-text-muted" style={{ marginLeft: '12px' }}>
            Showing {instances.length} restored instances
            {loading && ' (updating from AWS...)'}
          </span>
        </div>
      </div>

      {/* Instances Table */}
      {instances.length === 0 ? (
        <div className="aws-empty-state">
          <div className="aws-empty-state-icon">🗄️</div>
          <h3>No restored instances found</h3>
          <p>You haven't restored any database instances yet. Go to the Snapshots tab to restore a snapshot.</p>
        </div>
      ) : (
        <table className="aws-table">
          <thead>
            <tr>
              <th>Instance ID</th>
              <th>Source Snapshot</th>
              <th>Status</th>
              <th>Endpoint</th>
              <th>Port</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {instances.map((instance) => (
              <tr key={instance.instanceId}>
                <td>
                  <a 
                    href="#" 
                    onClick={(e) => { 
                      e.preventDefault(); 
                      setSelectedInstanceId(instance.instanceId); 
                    }}
                    style={{ 
                      color: '#0972d3', 
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                    title="Click to view instance details"
                  >
                    {instance.instanceId}
                  </a>
                </td>
                <td>{instance.snapshotId}</td>
                <td>
                  <span 
                    className={`aws-status-badge aws-status-${
                      instance.status === 'available' ? 'available' : 
                      instance.status === 'creating' ? 'creating' : 'error'
                    }`}
                    title={instance.status}
                  >
                    {instance.status}
                  </span>
                </td>
                <td>
                  {instance.endpoint ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{ 
                          color: '#0972d3', 
                          fontSize: '14px'
                        }}
                      >
                        {instance.endpoint}
                      </span>
                      <button
                        className="aws-copy-button"
                        onClick={(e) => copyEndpoint(instance.endpoint, e)}
                        title="Copy endpoint"
                      >
                        📋
                      </button>
                    </div>
                  ) : (
                    <span className="aws-text-muted">Not available</span>
                  )}
                </td>
                <td>
                  {instance.port || <span className="aws-text-muted">N/A</span>}
                </td>
                <td>
                  {new Date(instance.createdAt).toLocaleDateString()} <br />
                  <span className="aws-text-muted" style={{ fontSize: '12px' }}>
                    {new Date(instance.createdAt).toLocaleTimeString()}
                  </span>
                </td>
                <td>
                  <button
                    className="aws-button aws-button-danger"
                    onClick={() => handleDeleteInstance(instance.instanceId)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Instance Details Modal */}
      {selectedInstanceId && (
        <InstanceDetailsModal
          instanceId={selectedInstanceId}
          onClose={() => setSelectedInstanceId(null)}
          awsCredentials={awsCredentials}
        />
      )}
    </div>
  );
};

export default RestoredInstances;